/**
 * 电商套图 · 管理后台配置 Tab（规划 LLM / 规划积分）
 */
import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import toast from '../../Toast';
import { llmPlatformAPI, type LlmModel } from '../../../baselines/llm/llmPlatformAPI';

interface SuiteConfig {
  planLlmEnabled: boolean;
  planLlmModelId: string | null;
  planLlmSystemPrompt: string | null;
  planCreditsPerCall: number;
}

export default function EcommerceSuiteConfigTab() {
  const [config, setConfig] = useState<SuiteConfig | null>(null);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      platformFetch('/admin/ecommerce-suite/config').then((res) => res.json()),
      llmPlatformAPI.listModels().then((res) => res.data).catch(() => [] as LlmModel[]),
    ])
      .then(([cfg, modelList]) => {
        setConfig(cfg.data);
        setModels(modelList.filter((m) => m.isActive));
      })
      .catch(() => toast.error('获取配置失败'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!config) return;
    try {
      setSaving(true);
      const res = await platformFetch('/admin/ecommerce-suite/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || '保存失败');
      }
      toast.success('配置已保存');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>;
  }
  if (!config) return <p className="text-center py-12 text-light-muted">加载失败</p>;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-light-border space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-light-text">AI 规划功能</h3>
            <p className="text-xs text-light-muted mt-1">关闭后用户侧规划与 AI 帮写将提示未启用</p>
          </div>
          <button
            type="button"
            onClick={() => setConfig({ ...config, planLlmEnabled: !config.planLlmEnabled })}
            className={`relative h-6 w-11 rounded-full transition ${config.planLlmEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${config.planLlmEnabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text mb-1.5">规划 LLM 模型</label>
          <select
            value={config.planLlmModelId || ''}
            onChange={(e) => setConfig({ ...config, planLlmModelId: e.target.value || null })}
            className="w-full rounded-xl border border-light-border bg-white px-3 py-2.5 text-sm text-light-text outline-none focus:border-accent-blue"
          >
            <option value="">请选择 LLM 模型</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}（{model.modelIdString}）{model.supportsJsonMode ? '' : ' · 不支持JSON'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-light-muted">建议选择支持 JSON 模式的模型；规划同时会消耗用户 LLM 流量包</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text mb-1.5">规划 System Prompt 覆盖（可选）</label>
          <textarea
            value={config.planLlmSystemPrompt || ''}
            onChange={(e) => setConfig({ ...config, planLlmSystemPrompt: e.target.value || null })}
            className="w-full h-32 rounded-xl border border-light-border bg-white px-3 py-2.5 text-sm text-light-text outline-none focus:border-accent-blue resize-y"
            placeholder="留空使用内置默认规划提示词"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text mb-1.5">规划积分单价（0 = 不额外收积分）</label>
          <input
            type="number"
            min={0}
            max={10000}
            value={config.planCreditsPerCall}
            onChange={(e) => setConfig({ ...config, planCreditsPerCall: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
            className="w-40 rounded-xl border border-light-border bg-white px-3 py-2.5 text-sm text-light-text outline-none focus:border-accent-blue"
          />
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl bg-accent-blue px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
